import axios from 'axios';
import { bookingActions, ElectronicTradeDocumentFormatType, ElectronicTradeDocumentType, ImperialMetric, SpecialServiceType } from '../features/bookingSlice';
import { CustomerDetails } from '../features/customerSlice';
import { refreshAccessToken } from '../components/inactivity-modal/InactivityModal';
import {ApiConfig, AppData} from '../features/appDataSlice';
import { Banner } from './ContentRetrieverUtilities';
import { WlBookingHistoryItem } from '../pages/account-page/white-label/bookings/WlBookingsPage-Utils';

export enum PackageLocation {
  FRONT = 'FRONT',
  NONE = 'NONE',
  REAR = 'REAR',
  SIDE = 'SIDE'
}

export interface CurrencyValue {
  value: number,
  currency: string
}

export interface BookingRequestBody {
  quoteId: string,
  customerAccountNumber: string,
  // ready date plus pickUpFrom
  shipmentDateTime: string,
  customerCloseTime: string,
  senderDetails: {
    contactDetails: {
      name: string,
      telephone: string
    },
    address: {
      organisation: string,
      countryCode: string,
      postalCode: string,
      streetLines: string[],
      city: string,
      stateOrCounty: string | null,
    },
    notificationDetails: {
      email: string,
      mobile: string
    }
  },
  collectionDetails: {
    contactDetails: {
      name: string,
      telephone: string
    },
    address: {
      organisation: string,
      countryCode: string,
      postalCode: string,
      streetLines: string[],
      city: string,
      stateOrCounty: string | null,
      residential: boolean
    },
    notificationDetails: {
      email: string,
      mobile: string
    }
  } | null,
  deliveryDetails: {
    contactDetails: {
      name: string,
      telephone: string
    },
    address: {
      organisation: string,
      countryCode: string,
      postalCode: string,
      streetLines: string[],
      city: string,
      stateOrCounty: string | null,
      residential: boolean
    },
    notificationDetails: {
      email: string,
      mobile: string
    }
  },
  packageLocation: PackageLocation,
  parcels?: [
    {
      dimensions: {
        length: number,
        width: number,
        height: number,
        unit: string
      },
      weight: {
        value: number,
        unit: string
      },
      description?: string | undefined
    }
  ],
  customerShippingReference: string,
  contentDescription: string,
  deliveryInstructions: string,
  label: {
      type: string,
      size: string
  },
  shipmentType: string,
  remarks: string,
  bookingConfirmationEmail: string,
  amiCarrierServiceName: string,
  specialServices: SpecialServiceType[] | null;
  customsDetails: {
    windsorFr: string | null;
    exportComplianceStatement: string | null;
    b13AFilingOption: string | null;
    statementType: string | null;
    amount: CurrencyValue,
    invoiceDetails: {
      declarationNotes?: string,
      declarationStatement: string | null,
      declarationStatementRequested?: {
        value: boolean,
        type: string
      },
      termsOfSale?: string,
      taxDetails?: [
        {
          type: string,
          party: string,
          value: string
        }
      ],
      exportReason: any,
      declaredCharges: [],
      invoiceDate: string | null,
      invoiceNumber: string | null,
      cpc: string | null
    },
    electronicTradeDetails?: {
      uploadedDocuments: {
        type: ElectronicTradeDocumentType,
        contentType: ElectronicTradeDocumentFormatType,
        documentContent: string
      }[]
    } | null,
    importerOfRecordDetails: {
      deliveryAddress: boolean,
      contactDetails: {
        name: string,
        telephone: string
      },
      address: {
        organisation: string,
        countryCode: string,
        postalCode: string,
        streetLines: string[],
        city: string,
        stateOrCounty: string | null,
      }
    },
    commodities: {
      commodityCode: string | null,
      description: string | null,
      countryOfOrigin: string | null,
      quantity: {
        value: string,
        unit: string
      } | null,
      unitPrice: CurrencyValue | null,
      weight: {
        value: string,
        unit: string
      } | null
    }[] | null,
    declaredValue: number | null
  },
  totalShipmentValue: CurrencyValue, // required for Domestic shipment insurance
  brokerDetails: {
    contactDetails: {
      name: string,
      telephone: string
    },
    address: {
      organisation: string,
      countryCode: string,
      postalCode: string,
      streetLines: string[],
      city: string,
      stateOrCounty: string | null,
    }
  } | null,
  logonEmail: string,
  unitOfCurrency: string,
  unitOfMeasurement: ImperialMetric
}

export async function retrieveQuotesCall(requestBody: any, config: ApiConfig) {
  await refreshAccessToken();
  let quotes;
  let quoteError;
  const url = config.headers['IdToken'] ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/quote` : `${process.env.REACT_APP_NEBE_API_URL}/v2/quote`
  await axios.post(
    url,
    requestBody,
    config
  ).then((response: any) => {
    quotes = response.data;
  }, (error) => {
    console.error('quote error', error)
    quoteError = error.message;
  })

  return quoteError ? quoteError : quotes;
}

export interface BookingHistoryItem {
  alerts?: [];
  amiReference: string;
  carrierName: string;
  consignmentDetailsList: {
    consignmentNumber?: string;
    parcelNumbers: string[];
  }[],
  consolidated?: boolean;
  customerShippingReference: string;
  deliveryDetails: {
    address: {
      city: string;
      countryCode: string;
      organisation: string;
      postalCode?: string;
      residential: boolean;
      stateOrCounty: string | null,
      streetLines?: [string];
    };
    contactDetails?: {
      companyName: string;
      contactName: string;
    };
    importerOfRecord: boolean;
    notificationDetails?: {};
  };
  pickupConfirmationCode?: string;
  pickupMessage?: string;
  regionId: string;
  shipmentDate: Date;
  shipmentId: string;
}

export async function getBookingHistory(config: ApiConfig, customer: CustomerDetails) {
  await refreshAccessToken();
  const url = config.headers['IdToken'] ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/bookings` : `${process.env.REACT_APP_NEBE_API_URL}/v2/retrieve-bookings/${customer.creditCheck.tmffPartyId}`
  let bookingHistory: BookingHistoryItem[] = [];
  await axios.get(
    url,
    config
  ).then((response: any) => {
    bookingHistory = response.data;
  }, (error) => {
    console.error('bookingHistory error', error)
  })

  return bookingHistory as BookingHistoryItem[];
}

export async function getShipmentTracking(
  config: ApiConfig,
  customer: CustomerDetails,
  shipment: BookingHistoryItem
) {
  await refreshAccessToken();
  const url = config.headers['IdToken'] ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/track/${shipment.amiReference}/${shipment.shipmentId}` : `${process.env.REACT_APP_NEBE_API_URL}/v2/tracking/${customer.creditCheck.tmffPartyId}/${shipment.amiReference}/${shipment.shipmentId}`
  let trackingInfo: any = [];
  await axios.get(
    url,
    config
  ).then((response: any) => {
    trackingInfo = response.data;
  }, (error) => {
    console.error('getShipmentTracking error', error)
  })

  return trackingInfo;
}

export async function bookingIGCall(requestBody: any, config: ApiConfig, dispatch: any) {
  await refreshAccessToken();
  let bookingResponse;
  const url = config.headers['IdToken'] ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/book` : `${process.env.REACT_APP_NEBE_API_URL}/v2/booking`
  await axios.post(
    url,
    requestBody,
    config
  ).then((response: any) => {
    bookingResponse = response.data;
    dispatch(bookingActions.setBookingResult(response.data));
  }, (error) => {
    bookingResponse = error.response.data;
    console.error('booking error', error)
  })

  return bookingResponse;
}

export async function getLabelCall(url: string, config: ApiConfig) {
  await refreshAccessToken();
  let label: {
    type?: 'text/plain' | 'application/pdf',
    data?: any,
    labelReady?: boolean
  } = {};

  await axios.get(
    url,
    config
    ).then((response: any) => {
      label = {
        type: response.headers['content-type'],
        data: response.data,
        labelReady: response.status === 200
      }
    }, (error) => {
      console.error('get label error', error)
    })
  return label;
}

export async function getDocumentCall(config: ApiConfig, customer: CustomerDetails, amiRef: string) {
  await refreshAccessToken();
  let document: {
    type?: 'text/plain' | 'application/pdf',
    data?: any,
    documentReady?: boolean
  } = {};

  await axios.get(
    `${process.env.REACT_APP_NEBE_API_URL}/v4/document?partyId=${customer.creditCheck.tmffPartyId}&amiReference=${amiRef}`,
    config
    ).then((response: any) => {
      document = {
        type: response.headers['content-type'],
        data: response.data,
        documentReady: response.status === 200
      }
    }, (error) => {
      console.error('get document error', error)
    })
  return document;
}

export async function getLocations(requestBody: any, carrier: string, config: ApiConfig) {
  await refreshAccessToken();
  let locations: any;
  await axios.post(
    `${process.env.REACT_APP_NEBE_API_URL}/v4/locations/${carrier}`,
    requestBody,
    config
  ).then((response: any) => {
    locations = response.data;
  }, (error) => {
    console.error('locations error', error)
  })
  return locations;
}

export async function refreshAuth(apiConfig: any) {
  let response;

  await axios.get(
    `${process.env.REACT_APP_NEBE_API_URL}/v2/nebe-auth-refresh`,
    apiConfig
  ).then((resp: any) => {
    response = resp;
  }, (error) => {
    response = false;
    console.error('Refresh Error', error);
  })

  return response as any;
}

export async function getCountries(config: ApiConfig) {
  let countries;
  await axios.get(
    `${process.env.REACT_APP_NEBE_API_URL}/v2/reference-data`,
    config
  ).then((response: any) => {
    countries = response.data.countries;
  }, (error) => {
    console.error('get label error', error)
  })
  return countries;
}

export async function getCountryInfo(config: ApiConfig, countryCode: string) {
  let returnedData;
  await axios.get(
    `${process.env.REACT_APP_NEBE_API_URL}/v2/reference-data?countryCode=${countryCode}`,
    config
  ).then((response: any) => {
    returnedData = response.data;
  }, (error) => {
    console.error('countryCode error: ', error)
  })
  return returnedData as any;
}

export async function getCurrencyData(config: ApiConfig, currencyCode: any) {
  let url;
  let returnedData;
  if (currencyCode !== null) {
    url = `${process.env.REACT_APP_NEBE_API_URL}/v2/reference/currency?currencyCode=${currencyCode}`
  } else {
    url = `${process.env.REACT_APP_NEBE_API_URL}/v2/reference/currency`
  }
  await axios.get(
    url,
    config
  ).then((response: any) => {
    returnedData = response.data;
  }, (error) => {
    console.error('currency data error: ', error)
  });
  return returnedData as any;
}

export async function getCurrencyConversionRate(config: ApiConfig, currencyA: string, currencyB: string) {
  let returnedData;
  await axios.post(
    `${process.env.REACT_APP_NEBE_API_URL}/v2/convert-currency`,
    {
      "currencyA": currencyA,
      "currencyB": currencyB
    },
    config
  ).then((response: any) => {
    returnedData = response.data;
  }, (error) => {
    console.error('currency conversion rate error: ', error)
  })
  return returnedData as any;
}

export async function getAddresses(branchId: string, config: ApiConfig) {
  let addresses: any;
  const url = config.headers['IdToken'] ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/customer-address` : `${process.env.REACT_APP_NEBE_API_URL}/v2/customer-address?branchId=${branchId}`
  await axios.get(
    url,
    config
  ).then((response: any) => {
    addresses = response.data;
  }, (error) => {
    console.error('getAddresses error', error)
  })
  return addresses;
}

export async function getAddressesByCountry(branchId: string, config: ApiConfig, countryCode: string) {
  let addresses: any;
  const url = config.headers['IdToken']
    ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/customer-address?countryCode=${countryCode}`
    : `${process.env.REACT_APP_NEBE_API_URL}/v2/customer-address?branchId=${branchId}&countryCode=${countryCode}`
  await axios.get(
    url,
    config
  ).then((response: any) => {
    addresses = response.data;
  }, (error) => {
    console.error('getAddressesByCountry error', error)
  })
  return addresses;
}

export async function addAddress(requestBody: any, config: ApiConfig) {
  let id;
  const url = config.headers['IdToken'] ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/customer-address` : `${process.env.REACT_APP_NEBE_API_URL}/v2/customer-address`
  await axios.post(
    url,
    requestBody,
    config
  ).then((response: any) => {
    id = response.data.id;
  }, (error) => {
    console.error('addAddress error', error)
  })
  return id;
}

export async function deleteAddress(addressId: string, config: ApiConfig) {
  let resp;
  const url = config.headers['IdToken'] ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/customer-address?id=${addressId}` : `${process.env.REACT_APP_NEBE_API_URL}/v2/customer-address?id=${addressId}`
  await axios.delete(
    url,
    config
  ).then((response: any) => {
    resp = response;
  }, (error) => {
    console.error('deleteAddress error', error)
  })
  return resp;
}

export async function updateAddress(requestBody: any, config: ApiConfig) {
  let resp;
  const url = config.headers['IdToken'] ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/customer-address` : `${process.env.REACT_APP_NEBE_API_URL}/v2/customer-address`
  await axios.put(
    url,
    requestBody,
    config
  ).then((response: any) => {
    resp = response.data;
  }, (error) => {
    console.error('updateAddress error', error)
  })
  return resp;
}

export async function placeManifestBooking(requestBody: any, config: ApiConfig, customer: any) {
  let response;
  await axios.post(
    `${process.env.REACT_APP_NEBE_API_URL}/v2/bulk-booking?countryCode=${customer.customerCountryCode}`,
    requestBody,
    config
  ).then(resp => {response = resp})
  .catch(err => {
    response = false;
    console.error('Manifest booking error: ', err)
  });
  return response;
}

export async function logError(requestBody: any, config: ApiConfig) {
  await axios.post(
    `${process.env.REACT_APP_NEBE_API_URL}/v2/logging`,
    requestBody,
    config
  ).then(resp => {console.log(resp)})
  .catch(err => {
    console.error('Logging error: ', err)
  });
}

export async function getThemeData(config: ApiConfig) {
  const url = config.headers['IdToken'] ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/theme` : `${process.env.REACT_APP_NEBE_API_URL}/v2/theme`;
  let response;
  await axios.get(
    url,
    config
  ).then(resp => response = resp)
  .catch(err => {
    console.error('getThemeData error: ', err)
  });
  return response;
}

export async function setThemeData(requestBody: any, config: ApiConfig, parentPartyId: string) {
  let response;
  await axios.put(
    `${process.env.REACT_APP_NEBE_API_URL}/v2/theme?parentPartyId=${parentPartyId}`,
    requestBody,
    config
  ).then(resp => response = resp)
  .catch(err => {
    console.error('putThemeData error: ', err)
  });
  return response;
}

export async function getTemplateData(config: ApiConfig) {
  let templates: any = [];
  const url = config.headers['IdToken'] ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/template` : `${process.env.REACT_APP_NEBE_API_URL}/v2/template`
  await axios.get(
    url,
    config
  ).then(resp => {
    templates = resp.data?.Item.templates
  })
  .catch(err => {
    console.error('getTemplateData error: ', err)
  });
  return templates;
}

export async function setTemplateData(requestBody: any, config: ApiConfig) {
  let response: any;
  const url = config.headers['IdToken'] ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/template` : `${process.env.REACT_APP_NEBE_API_URL}/v2/template`
  await axios.put(
    url,
    requestBody,
    config
  ).then(resp => response = resp)
  .catch(err => {
    console.error('putTemplateData error: ', err)
  });
  return response;
}

export async function getChildCustomerData(config: ApiConfig, parentPartyId: string, childPartyId: string) {
  let response;
  await axios.get(
    `${process.env.REACT_APP_NEBE_API_URL}/v2/child-customer-data?parentPartyId=${parentPartyId}&childPartyId=${childPartyId}`,
    config
  ).then(resp => response = resp)
  .catch(err => {
    console.error('getChildCustomerData error: ', err)
  });
  return response;
}

export async function getWlMarkup(config: ApiConfig, entityId: string) {
  let response;
  await axios.get(
    `${process.env.REACT_APP_NEBE_API_URL}/white-label/direct/v1/markup/${entityId}`,
    config
  )
  .then(resp => response = resp)
  .catch(err => {
    response = err.response
    console.error('getWlMarkup error: ', err)
  });
  return response;
}

export async function setWlMarkup(requestBody: any, config: ApiConfig) {
  let response;
  await axios.post(
    `${process.env.REACT_APP_NEBE_API_URL}/white-label/direct/v1/markup`,
    requestBody,
    config
  ).then(resp => response = resp)
  .catch(err => {
    console.error('setWlMarkup error: ', err)
  });
  return response;
}

export async function setWlImage(requestBody: any, extension: string, config: ApiConfig, type: 'banner' | 'logo') {
  let response;
  await axios.put(
    `${process.env.REACT_APP_NEBE_API_URL}/v2/manage-content?type=${type}&fileType=${extension}`,
    requestBody,
    config
  ).then(resp => response = resp)
  .catch(err => {
    console.error('setWlImage error: ', err)
  });
  return response;
}

export async function getWlBookings(config: ApiConfig) {
  let response;
  await axios.get(
    `${process.env.REACT_APP_NEBE_API_URL}/white-label/direct/v1/bookings/indirect-bookings`,
    config
  ).then(resp => response = resp.data)
  .catch(err => {
    console.error('getWlBookings error: ', err)
  });
  return response;
}

export async function getFullWlBookingsData(config: ApiConfig) {
  let response;
  await axios.get(
    `${process.env.REACT_APP_NEBE_API_URL}/white-label/direct/v1/bookings/indirect-bookings-full`,
    config
  ).then(resp => response = resp.data)
  .catch(err => {
    console.error('getFullWlBookingsData error: ', err)
  });
  return response;
}

export async function setWlBanners(
  config: ApiConfig,
  countryCode: string,
  page: 'home' | 'quote',
  parentPartyId: string,
  banners: Banner[]
) {
  let response;
  await axios.post(
    `${process.env.REACT_APP_NEBE_API_URL}/v2/manage-content?countryOfOrigin=${countryCode}&page=announcement-banner-${page}-page-${parentPartyId}&parentPartyId=${parentPartyId}`,
    banners,
    config
  ).then(resp => response = resp.data)
  .catch(err => {
    console.error('setWlBanners error: ', err)
    response = err.response.data
  });
  return response;
}

export async function postWlEntity(requestBody: any, config: ApiConfig) {
  let response;
  await axios.post(
    `${process.env.REACT_APP_NEBE_API_URL}/white-label/direct/v1/entity`,
    requestBody,
    config
  ).then(resp => response = resp)
  .catch(err => {
    console.error('postWlEntity() error: ', err)
  });
  return response;
}

export async function whiteLabelSignIn(email: string, password: string) {
  let response;

  const requestBody = {
    email: email,
    password: password
  };

  await axios.post(
    `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/auth`,
    requestBody
  ).then((resp) => {
    response = resp;
  }).catch(err => {
    response = err.response;
    console.error("sign in error: ", err)
  })

  return response as any;
}

export async function getWlEntities(config: ApiConfig) {
  let response;
  await axios.get(
    `${process.env.REACT_APP_NEBE_API_URL}/white-label/direct/v1/entity`,
    config
  ).then(resp => response = resp)
  .catch(err => {
    console.error('getWlEntities() error: ', err)
  });
  return response;
}

export async function getWlUsersByEntityId(entityId: string, config: ApiConfig) {
  let response;
  await axios.get(
    `${process.env.REACT_APP_NEBE_API_URL}/white-label/direct/v1/user/${entityId}`,
    config
  ).then(resp => response = resp)
  .catch(err => {
    console.error('getWlUsersByEntityId() error: ', err)
  });
  return response;
}

export async function createWlUser(body: any, config: ApiConfig) {
  let response;
  await axios.post(
    `${process.env.REACT_APP_NEBE_API_URL}/white-label/direct/v1/user`,
    body,
    config
  ).then(resp => response = resp)
  .catch(err => {
    console.error('createWlUser error: ', err)
  });
  return response;
}

export async function updateWlUser(body: any, config: ApiConfig, userId: string) {
  let response;
  await axios.put(
    `${process.env.REACT_APP_NEBE_API_URL}/white-label/direct/v1/user/${userId}`,
    body,
    config
  ).then(resp => response = resp)
  .catch(err => {
    console.error('updateWlUser() error: ', err)
  });
  return response;
}

export async function getWlCustomerShipmentTracking(booking: WlBookingHistoryItem, config: ApiConfig) {
  let response;
  await axios.get(
    `${process.env.REACT_APP_NEBE_API_URL}/white-label/direct/v1/track/${booking.reference}/${booking.carrierShippingReference}`,
    config
  ).then(resp => response = resp)
  .catch(err => {
    console.error('getWlCustomerShipmentTracking() error: ', err)
  });
  return response as any;
}